/**
 * Generic in-game purchase celebration — sparkle burst + flying doober icon
 * + rising label. Listened to by `PurchaseCelebrationLayer` mounted inside
 * `AppShell`, so any screen with the shell visible can fire a celebration.
 *
 * Coin/gem flies to the HUD use the existing `coinFlyToHud.ts`; this adds
 * the *non-currency* doober (e.g. an unlocked recipe icon) plus the
 * particle-burst-and-rising-label feel-good moment.
 */

export const CWF_PURCHASE_CELEBRATION_EVENT = "cwf-purchase-celebration";

export type CelebrationAccent = "gold" | "gem" | "energy" | "rose";

export type CelebrationSourcePoint = {
  /** Viewport-relative center point of the burst origin. */
  x: number;
  y: number;
};

export type PurchaseCelebrationDetail = {
  /** Sprite that flies (recipe dish icon, item icon, etc.). */
  iconSrc: string;
  /** Origin point in viewport coords (usually rect center of the buy button). */
  source: CelebrationSourcePoint;
  /**
   * Optional DOM selector for the destination — the doober arcs toward the
   * center of the matched element. If omitted, the doober rises and fades.
   */
  targetSelector?: string;
  /** Floating label that rises above the burst (e.g. "Quesadilla unlocked!"). */
  label?: string;
  /** Sparkle / ring tint. Default `gold`. */
  accent?: CelebrationAccent;
  /** Skip the win SFX (e.g. when bursting multiple at once). */
  silent?: boolean;
};

/** Fire-and-forget — safe to call from inside React handlers / mutation callbacks. */
export function emitPurchaseCelebration(detail: PurchaseCelebrationDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<PurchaseCelebrationDetail>(CWF_PURCHASE_CELEBRATION_EVENT, {
      detail,
    }),
  );
}

/** Convenience: derive a viewport-center source from any DOM element. */
export function celebrationSourceFromElement(
  el: Element | null | undefined,
): CelebrationSourcePoint {
  if (!el || typeof window === "undefined") {
    return {
      x: typeof window === "undefined" ? 0 : window.innerWidth / 2,
      y: typeof window === "undefined" ? 0 : window.innerHeight / 2,
    };
  }
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}
